import { combineReducers, configureStore } from '@reduxjs/toolkit';
import {
  headerSlice,
  dictionarySlice,
  activeSideMenuSlice,
  userInfoSlice,
  notificationsSlice,
} from './slices';
import { setupListeners } from '@reduxjs/toolkit/query';

import { userInfoApi } from './api/userApiSlice';
import { dictionaryApi } from './api/dictionaryApiSlice';

import fileUploadSlice from './slices/fileUploadSlice';

import { basicHrmRequestApi } from './api/basicHrmApiSlice';

import modalSlice from './slices/modalSlice';
import { dmsApi } from './api/dmsApiSlice';
import filterOptionsSlice from './slices/filterOptionsSlice';
import manageRequestSlice from '@Features/store/ManageRequestSlice';
import { attendanceRequestApi } from '@Features/EmployeeSelfService/requests/AttendanceRequest/store/api/attendanceRequestApiSlice';
import attendanceRequestSlice from '@Features/EmployeeSelfService/requests/AttendanceRequest/store/attendanceRequestSlice';
import { itRequisitionApi } from '@Features/EmployeeSelfService/requests/ITRequisition/store/api/itRequisitionApiSlice';
import itRequisitionSlice from '@Features/EmployeeSelfService/requests/ITRequisition/store/ITRequisitionSlice';
import { leaveRequestApi } from '@Features/EmployeeSelfService/requests/LeaveRequest/store/api/leaveRequestApiSlice';
import leaveRequestSlice from '@Features/EmployeeSelfService/requests/LeaveRequest/store/leaveRequestSlice';
import { overseasMissionApi } from '@Features/EmployeeSelfService/requests/OverseasMission/store/api/overseasRequestApiSlice';
import overseasMissionSlice from '@Features/EmployeeSelfService/requests/OverseasMission/store/overseasMissionSlice';
import { passportRequestApi } from '@Features/EmployeeSelfService/requests/PassportRequest/store/api/passportRequestApiSlice';
import passportRequestSlice from '@Features/EmployeeSelfService/requests/PassportRequest/store/passportRequestSlice';
import { resignationRequestApi } from '@Features/EmployeeSelfService/requests/ResignationRequests/store/api/resignationRequestApiSlice';
import resignationRequestSlice from '@Features/EmployeeSelfService/requests/ResignationRequests/store/resignationRequestSlice';
import { resumptionRequestApi } from '@Features/EmployeeSelfService/requests/ResumptionRequests/store/api/resumptionRequestApiSlice';
import resumptionRequestSlice from '@Features/EmployeeSelfService/requests/ResumptionRequests/store/resumptionRequestSlice';
import { salaryCertificateApi } from '@Features/EmployeeSelfService/requests/SalaryCertificate/store/api/salaryCertificateApiSlice';
import salaryCertificateSlice from '@Features/EmployeeSelfService/requests/SalaryCertificate/store/salaryRequestSlice';
import { materialRequestApi } from '@Features/MaterialRequest/requests/MaterialRequisition/store/api/MaterialRequestApiSlice';
import materialRequestSlice from '@Features/MaterialRequest/requests/MaterialRequisition/store/materialRequestSlice';
import approvalsListSlice from './slices/approvalsListSlice';
import { approvalsListApi } from './api/approvalsListApiSlice';
import { strapiApi } from './api/strapiApiSlice';
import authSlice from './slices/authSlice';
import { notificationsApi } from './api/notificationsApiSlice';
import { permissionsApi } from './api/permissionsApiSlice';
import { tenderRequestApi } from '@Features/MaterialRequest/requests/TenderRequest/store/api/TenderRequestApiSlice';
import { purchaseRequestApi } from '@Features/MaterialRequest/requests/PurchaseRequest/store/api/purchaseRequestApiSlice';
import { chatbotApi } from './api/chatbotApiSlice';
import { contactApi } from './api/contactApiSlice';
import purchaseRequestSlice from '@Features/MaterialRequest/requests/PurchaseRequest/store/purchaseRequestSlice';
import eventMessageSlice from './slices/eventMessageSlice';
import { profileApi } from './api/profileApiSlice';

const common = combineReducers({
  header: headerSlice.reducer,
  user: userInfoSlice.reducer,
  dictionary: dictionarySlice.reducer,
  activeSideMenu: activeSideMenuSlice.reducer,
  fileUpload: fileUploadSlice.reducer,
  modal: modalSlice.reducer,
  filterOptions: filterOptionsSlice.reducer,
  approvalsList: approvalsListSlice.reducer,
  notifications: notificationsSlice.reducer,
  eventMessageType: eventMessageSlice.reducer,
});

const featureStores = combineReducers({
  leaveRequest: leaveRequestSlice.reducer,
  itRequisition: itRequisitionSlice.reducer,
  passportRequest: passportRequestSlice.reducer,
  materialRequest: materialRequestSlice.reducer,
  purchaseRequest: purchaseRequestSlice.reducer,
  salaryCertificate: salaryCertificateSlice.reducer,
  overseasMission: overseasMissionSlice.reducer,
  manageRequest: manageRequestSlice.reducer,
  resumptionRequest: resumptionRequestSlice.reducer,
  attendanceRequest: attendanceRequestSlice.reducer,
  resignationRequest: resignationRequestSlice.reducer,
});

export const store = configureStore({
  reducer: {
    auth: authSlice.reducer,
    common,
    featureStores,
    [leaveRequestApi.reducerPath]: leaveRequestApi.reducer,
    [passportRequestApi.reducerPath]: passportRequestApi.reducer,
    [userInfoApi.reducerPath]: userInfoApi.reducer,
    [dictionaryApi.reducerPath]: dictionaryApi.reducer,
    [approvalsListApi.reducerPath]: approvalsListApi.reducer,
    [itRequisitionApi.reducerPath]: itRequisitionApi.reducer,
    [materialRequestApi.reducerPath]: materialRequestApi.reducer,
    [salaryCertificateApi.reducerPath]: salaryCertificateApi.reducer,
    [overseasMissionApi.reducerPath]: overseasMissionApi.reducer,
    [resumptionRequestApi.reducerPath]: resumptionRequestApi.reducer,
    [basicHrmRequestApi.reducerPath]: basicHrmRequestApi.reducer,
    [attendanceRequestApi.reducerPath]: attendanceRequestApi.reducer,
    [dmsApi.reducerPath]: dmsApi.reducer,
    [resignationRequestApi.reducerPath]: resignationRequestApi.reducer,
    [strapiApi.reducerPath]: strapiApi.reducer,
    [notificationsApi.reducerPath]: notificationsApi.reducer,
    [permissionsApi.reducerPath]: permissionsApi.reducer,
    [tenderRequestApi.reducerPath]: tenderRequestApi.reducer,
    [purchaseRequestApi.reducerPath]: purchaseRequestApi.reducer,
    [chatbotApi.reducerPath]: chatbotApi.reducer,
    [contactApi.reducerPath]: contactApi.reducer,
    [profileApi.reducerPath]: profileApi.reducer,
  },
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: false,
    }).concat([
      userInfoApi.middleware,
      leaveRequestApi.middleware,
      dictionaryApi.middleware,
      approvalsListApi.middleware,
      itRequisitionApi.middleware,
      passportRequestApi.middleware,
      materialRequestApi.middleware,
      salaryCertificateApi.middleware,
      overseasMissionApi.middleware,
      resumptionRequestApi.middleware,
      basicHrmRequestApi.middleware,
      attendanceRequestApi.middleware,
      dmsApi.middleware,
      resignationRequestApi.middleware,
      strapiApi.middleware,
      permissionsApi.middleware,
      tenderRequestApi.middleware,
      purchaseRequestApi.middleware,
      chatbotApi.middleware,
      contactApi.middleware,
      profileApi.middleware,
    ]),
});

setupListeners(store.dispatch);

// Infer the `RootState` and `AppDispatch` types from the store itself
export type RootState = ReturnType<typeof store.getState>;
// Inferred type: {posts: PostsState, comments: CommentsState, users: UsersState}
export type AppDispatch = typeof store.dispatch;

export default store;
